import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { connect } from 'react-redux';
import { Redirect } from 'react-router-dom';
import { HelpText } from 'src/components/atoms';
import { PasswordHelpText } from 'src/components/organisms/ChangePasswordNew/ChangePasswordNew.styles';
import { validateNewPassword } from 'src/components/organisms/ChangePasswordNew/passwordRules';
import * as OpenReportingCreators from '../../../creators/OpenReporting.creators';
import { createSession } from '../../../creators/User.creators';
import '../../../css/public/PublicReporting.css';
import I18next from '../../../i18n';
import * as Types from '../../../types';
import { Footer } from '../../moleculesLegacy/Footer';
import { TextBox } from '../../organismsLegacy/TextBox';
import CustomHeader from '../CustomHeader';
import MobileBackButton from '../MobileBackButton';
import ProgressBar from '../ProgressBar';
import ResponsiveContainer from '../ResponsiveContainer';
import SpamChallenge from '../SpamChallenge/SpamChallenge';
import {
  CustomHeaderContainer,
  PublicReportingContainer,
} from '../questions/QuestionContainer/QuestionContainer.styles';
import {
  CurrentPasswordContainer,
  HeadingContainer,
  InfoContainer,
  OpenReportHeading,
  PasswordSectionTitle,
} from './Password.styles';

type Props = ReturnType<typeof mergeProps>;

type ownProps = {
  submit: () => Promise<void>;
  changePassword: boolean;
  currentUser?: Types.User;
  isLocalised?: boolean;
};

const MIN_PASSWORD_LENGTH = 9;
const MIN_PASSWORD_INPUT = 3;

const Password = (props: Props) => {
  const { t } = useTranslation();
  const onSubmit = async () => {
    if (cannotSubmit) {
      return false;
    }

    await props.submit();
    props.clearPublicReportData();
  };

  const invalidPasswords = (
    invalidPasswordMessage: string,
    newPassword: string,
    newPasswordConfirmation: string,
  ) =>
    invalidPasswordMessage.length !== 0 ||
    newPassword !== newPasswordConfirmation;

  const validPassword = () => {
    if (props.changePasswordValues?.newPassword.length) {
      return validateNewPassword(
        props.changePasswordValues.newPassword ?? '',
      ).every((rule) => rule[2]);
    }
    return validateNewPassword(props.password ?? '').every((rule) => rule[2]);
  };

  const validConfirmation = (confirmNewPassword: string, newPassword: string) =>
    confirmNewPassword.length === 0 || confirmNewPassword === newPassword;

  const confirmNewPasswordEntered = (confirmNewPassword: string) =>
    confirmNewPassword.length > 0;

  const cannotSubmit =
    props.changePassword && props.changePasswordValues
      ? invalidPasswords(
          props.invalidPasswordMessage,
          props.changePasswordValues?.newPassword,
          props.changePasswordValues?.confirmNewPassword,
        )
      : invalidPasswords(
          props.invalidPasswordMessage,
          props.publicReport?.password ?? '',
          props.publicReport?.confirmation ?? '',
        ) || props.solutionToken?.length === 0;

  const inputStyle: React.CSSProperties = {
    width: '100%',
    paddingTop: 5,
    paddingBottom: 5,
  };

  const mismatchedPasswords = () => {
    const password =
      props.changePassword && props.changePasswordValues
        ? props.changePasswordValues.newPassword
        : (props.publicReport?.password ?? '');
    const confirmation =
      props.changePassword && props.changePasswordValues
        ? props.changePasswordValues.confirmNewPassword
        : (props.publicReport?.confirmation ?? '');
    return Boolean(
      password !== confirmation &&
      password.length > MIN_PASSWORD_INPUT &&
      confirmation.length > MIN_PASSWORD_LENGTH,
    );
  };

  const invalidPassword =
    confirmNewPasswordEntered(
      props.changePassword && props.changePasswordValues
        ? props.changePasswordValues.confirmNewPassword
        : (props.publicReport?.confirmation ?? ''),
    ) && props.invalidPasswordMessage;

  return (
    <PublicReportingContainer>
      {!props.changePassword && props.redirect ? (
        <Redirect to={`/${props.organisation}/open-reporting/success`} />
      ) : null}

      <CustomHeaderContainer>
        <CustomHeader
          organisation={props.organisation}
          currentUser={props.currentUser}
          hideID
        />
      </CustomHeaderContainer>

      <ResponsiveContainer
        includeCloseCircleButton={props.changePassword}
        includeLeftCircleButton={!props.changePassword}
        includeRightCircleButton={!props.changePassword}
        includeBottomButton={true}
        backLinkTo={`/${props.organisation}/open-reporting/${
          props.changePassword ? 'reports' : 'details'
        }`}
        nextLinkTo={'#'}
        nextLinkDisabled={cannotSubmit}
        buttonText={t(
          `Buttons.${props.changePassword ? 'changePassword' : 'submit'}`,
        )}
        buttonStyles={{ textTransform: 'uppercase' }}
        nextButtonOnClick={onSubmit}
        setMinHeight={false}
      >
        <HeadingContainer changePassword={props.changePassword}>
          {props.changePassword && (
            <MobileBackButton
              backText={
                props.changePassword
                  ? 'Buttons.back'
                  : 'public.PublicReport.backToReports'
              }
              backTo={`/${props.organisation}/open-reporting/reports`}
              $changePassword={props.changePassword}
            />
          )}
          <OpenReportHeading>
            {t(
              `public.Password.${
                props.changePassword
                  ? 'changePasswordHeader'
                  : 'createPasswordHeader'
              }`,
            )}
          </OpenReportHeading>
          {!props.changePassword && <ProgressBar percentage={100} />}
        </HeadingContainer>

        <div className="responsive-container-body">
          {!props.changePassword && (
            <h3
              className="public-reporting-subheading"
              style={{ marginLeft: -8, marginRight: -8, paddingBottom: 20 }}
            >
              {t('public.Password.subtitle')}
            </h3>
          )}
          <InfoContainer changePassword={props.changePassword}>
            <img
              alt=""
              className="icon-image-text"
              src="/assets/icon_info_green.png"
            />
            <p
              className="public-reporting-detail start"
              style={{
                margin: props.changePassword ? 0 : '15px 0',
                fontSize: '12px',
              }}
            >
              {t('public.Password.newDescription')}
            </p>
          </InfoContainer>
          <div className="grow-container">
            {props.changePassword && (
              <CurrentPasswordContainer>
                <PasswordSectionTitle>
                  {t('public.Password.currentPasswordPlaceholder')}
                </PasswordSectionTitle>

                <TextBox
                  id="public-reporting-current-password"
                  data-testid="public-reporting-current-password"
                  password={true}
                  style={inputStyle}
                  valid={true}
                  value={
                    props.changePasswordValues
                      ? props.changePasswordValues.currentPassword
                      : ''
                  }
                  placeholder={t('public.Password.currentPasswordPlaceholder')}
                  onChange={props.setCurrent}
                />
              </CurrentPasswordContainer>
            )}

            <PasswordSectionTitle>
              {t('public.Password.createNewPassword')}
            </PasswordSectionTitle>
            <TextBox
              id="public-reporting-password"
              data-testid="public-reporting-password"
              password={true}
              style={inputStyle}
              valid={
                props.changePassword && props.changePasswordValues
                  ? validPassword()
                  : props.invalidPasswordMessage.length === 0 &&
                    props.publicReport?.password.length !== 0
              }
              value={
                props.changePassword && props.changePasswordValues
                  ? props.changePasswordValues.newPassword
                  : (props.publicReport?.password ?? '')
              }
              placeholder={t('public.Password.passwordPlaceholder')}
              onChange={props.setPassword}
            />
            {errorHandler(props) as React.ReactNode}

            <TextBox
              id="public-reporting-confirmation"
              data-testid="public-reporting-confirmation"
              password={true}
              style={{ ...inputStyle, marginTop: 30 }}
              valid={
                props.changePassword && props.changePasswordValues
                  ? validConfirmation(
                      props.changePasswordValues.confirmNewPassword,
                      props.changePasswordValues.newPassword,
                    )
                  : validConfirmation(
                      props.publicReport?.confirmation ?? '',
                      props.publicReport?.password ?? '',
                    )
              }
              value={
                props.changePassword && props.changePasswordValues
                  ? props.changePasswordValues.confirmNewPassword
                  : (props.publicReport?.confirmation ?? '')
              }
              placeholder={t('public.Password.reenterPlaceholder')}
              onChange={props.setConfirmation}
              enterKeyAction={() => onSubmit()}
            />
            {errorHandlerMismatch(
              invalidPassword,
              mismatchedPasswords(),
              props,
            )}

            {!props.changePassword && <SpamChallenge />}
          </div>
        </div>
      </ResponsiveContainer>
      <Footer id="desktop-powered-by-vault" />
    </PublicReportingContainer>
  );
};

const mapStateToProps = (state: Types.State, ownProps: ownProps) => {
  const openReporting = {
    changePasswordValues: state.openReporting.changePasswordReducer,
    publicReport: state.openReporting.openReportReducer,
    ...state.openReporting?.openReportingReducer,
  };
  const passwordInvalid = ownProps.changePassword
    ? !validateNewPassword(
        openReporting.changePasswordValues?.newPassword ?? '',
      ).every((rule) => rule[2])
    : !validateNewPassword(openReporting.publicReport?.password ?? '').every(
        (rule) => rule[2],
      );

  return {
    organisation: openReporting.publicReport?.organisation,
    publicReport: openReporting.publicReport,
    invalidPasswordMessage: passwordInvalid ? 'Error' : '',
    redirect: openReporting.redirect,
    username: openReporting.publicReporterAccount
      ? openReporting.publicReporterAccount.username
      : '',
    password: openReporting.publicReport?.password,
    changePasswordValues: openReporting.changePasswordValues,
    solutionToken: openReporting.solutionToken,
  };
};

const mapDispatchToProps = {
  createSession,
  ...OpenReportingCreators,
};

const mergeProps = (
  stateProps: ReturnType<typeof mapStateToProps>,
  dispatchProps: typeof mapDispatchToProps,
  ownProps: ownProps,
) => ({
  ...ownProps,
  ...dispatchProps,
  ...stateProps,
  setPassword: ownProps.changePassword
    ? dispatchProps.setNewPassword
    : dispatchProps.setPassword,
  setConfirmation: ownProps.changePassword
    ? dispatchProps.setConfirmNewPassword
    : dispatchProps.setConfirmation,
  setCurrent: dispatchProps.setCurrentPassword,
});

export default connect(
  mapStateToProps,
  mapDispatchToProps,
  mergeProps,
)(Password);

function errorHandler(
  props: ReturnType<typeof mergeProps>,
):
  | (React.ReactNode | Record<string, unknown>)
  | Iterable<React.ReactNode | Record<string, unknown>> {
  const isChangePasswordValues =
    props.changePasswordValues &&
    props.changePasswordValues.newPassword.length > MIN_PASSWORD_INPUT;
  const isCreatePasswordValues =
    props.password && props.password.length > MIN_PASSWORD_INPUT;

  return (
    <PasswordHelpText
      $margin="8px 0"
      $showHelperText={Boolean(
        isChangePasswordValues || isCreatePasswordValues,
      )}
    >
      {(props.changePasswordValues?.newPassword.length
        ? validateNewPassword(props.changePasswordValues?.newPassword ?? '')
        : validateNewPassword(props.password ?? '')
      ).map(([text, testid, valid, translatedText], index) => (
        <HelpText
          key={index}
          state={valid ? Types.FormState.SUCCESS : Types.FormState.ERROR}
          helpText={props.isLocalised ? translatedText : text}
          data-testid={testid}
          textSize="medium"
          $margin="0"
        />
      ))}
    </PasswordHelpText>
  );
}

function errorHandlerMismatch(
  invalidPassword: string | false,
  mismatchedPasswords: string | boolean,
  props: ReturnType<typeof mergeProps>,
) {
  if (invalidPassword || mismatchedPasswords) {
    return (
      <PasswordHelpText $showHelperText={true}>
        <HelpText
          state={Types.FormState.ERROR}
          data-testid="passwords-mismatch"
          helpText={
            props.isLocalised
              ? I18next.t('validatorsNew.passwordMissMatch')
              : 'Passwords don’t match'
          }
          textSize="medium"
          $margin="0"
        />
      </PasswordHelpText>
    );
  }
}
